// useDeviceData.ts
import { useState, useEffect } from 'react';

interface DeviceData {
  ats: number;
  v: number;
  di: string;
  dt: string;
}

const calculateDates = (range: string) => {
  const now = new Date();
  const endDate = now.toISOString();
  let startDate = '';

  switch (range) {
    case 'last60Minutes':
      startDate = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
      break;
    case 'last12Hours':
      startDate = new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString();
      break;
    case 'last3Days':
      startDate = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString();
      break;
    case 'last7Days':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      break;
    case 'last30Days':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      break;
    default:
      startDate = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
  }

  return { startDate, endDate };
};

const fetchDeviceData = async (
  deviceIds: string[],
  startDate: string,
  endDate: string,
  interval: string
): Promise<DeviceData[]> => {
  const results = await Promise.all(
    deviceIds.map(deviceId =>
      fetch(
        `https://iot-rest-prod.urban.io/api/v1/device/${deviceId}/readings?start=${startDate}&end=${endDate}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'X-User-Email': process.env.REACT_APP_X_USER_EMAIL!,
            'X-User-Token': process.env.REACT_APP_X_USER_TOKEN!,
          },
        }
      ).then(res => res.json())
    )
  );
  return results;
};

const useDeviceData = (
  deviceIds: string[],
  timeRange: string,
  interval: string
) => {
  const [data, setData] = useState<DeviceData[][]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastParams, setLastParams] = useState<{ ids: string[]; range: string; interval: string } | null>(null);

  useEffect(() => {
    if (deviceIds.length === 0) return;

    const isSame =
      lastParams?.range === timeRange &&
      lastParams?.interval === interval &&
      JSON.stringify(lastParams?.ids) === JSON.stringify(deviceIds);

    if (isSame) return; // Avoid refetch if nothing changed

    const { startDate, endDate } = calculateDates(timeRange);

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await fetchDeviceData(deviceIds, startDate, endDate, interval);
        setData(result);
        setLastParams({ ids: deviceIds, range: timeRange, interval });
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [deviceIds, timeRange, interval, lastParams]);

  return { data, isLoading, error };
};

export default useDeviceData;
